import './landings.css';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { kindInfo, lifecycleOf, pageKinds, type LandingListItem } from '@agency/contracts';
import { Badge, Button, Drawer, EmptyState, Notice, SelectField, Skeleton, TextField, scrollRegion, useToast } from '@agency/ui';
import { ApiClientError, get, post } from '../../lib/api.ts';
import { fmtDate } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';
import { useAuth } from '../../lib/auth.tsx';
import { DuplicateDrawer, NewLandingDrawer, TranslateDrawer } from './landing-dialogs.tsx';

const LIFECYCLE: Record<ReturnType<typeof lifecycleOf>, { label: string; tone: 'neutral' | 'info' | 'success' | 'warning' }> = {
  draft: { label: 'Draft only', tone: 'neutral' },
  review: { label: 'In review', tone: 'info' },
  scheduled: { label: 'Scheduled', tone: 'info' },
  published: { label: 'Published', tone: 'success' },
  'published-changes': { label: 'Unpublished changes', tone: 'warning' },
  archived: { label: 'Archived', tone: 'neutral' },
};
const TRANSLATION: Record<string, string> = { NOT_STARTED: 'Not started', DRAFT: 'Draft', NEEDS_REVIEW: 'Needs review', APPROVED: 'Approved', PUBLISHED: 'Published' };

/** Disclosure menu: native details so it works without extra focus plumbing; closes after an action. */
function RowMenu({ label, children }: { label: string; children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <details className="rowmenu" open={open} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)} onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
      <summary className="ag-btn ag-btn--sm" aria-label={label}>More</summary>
      <div className="rowmenu__list" role="group" aria-label={label}>{children(() => setOpen(false))}</div>
    </details>
  );
}

export function LandingsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [newOpen, setNewOpen] = useState(false);
  const [dup, setDup] = useState<LandingListItem | null>(null);
  const [tr, setTr] = useState<LandingListItem | null>(null);
  const [archive, setArchive] = useState<LandingListItem | null>(null);
  const q = useQuery<LandingListItem[], ApiClientError>({ queryKey: ['landings'], queryFn: () => get('/admin/landings') });

  const search = params.get('q') ?? '';
  const kind = params.get('kind') ?? '';
  const locale = params.get('locale') ?? '';
  const life = params.get('state') ?? '';
  const setFilter = (k: string, v: string) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });

  const all = q.data ?? [];
  const locales = useMemo(() => [...new Set(all.map((l) => l.locale))].sort(), [all]);
  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return all.filter((l) => {
      const lc = lifecycleOf(l);
      if (!life && lc === 'archived') return false; // archived pages stay out of the way until asked for
      if (life && lc !== life) return false;
      if (kind && l.kind !== kind) return false;
      if (locale && l.locale !== locale) return false;
      return !s || l.name.toLowerCase().includes(s) || l.slug.toLowerCase().includes(s);
    });
  }, [all, search, kind, locale, life]);
  const filtered = Boolean(search || kind || locale || life);

  const archiveM = useMutation<void, ApiClientError, { item: LandingListItem; restore: boolean }>({
    mutationFn: ({ item, restore }) => post(`/admin/landings/${item.id}/${restore ? 'unarchive' : 'archive'}`),
    onSuccess: (_r, { item, restore }) => { void qc.invalidateQueries({ queryKey: ['landings'] }); toast({ tone: 'success', message: restore ? `${item.name} restored as a draft.` : `${item.name} archived. Nothing was deleted.` }); setArchive(null); },
  });

  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Landings</h1>
          <p>Every page on the website. Edit a draft, then publish it as a new immutable version; leads keep the exact version they came from.</p>
        </div>
        {can('landings.create') ? <Button variant="primary" icon="plus" onClick={() => setNewOpen(true)}>New landing</Button> : null}
      </div>

      <form className="filters" role="search" aria-label="Filter landings" onSubmit={(e) => e.preventDefault()}>
        <TextField label="Search" type="search" value={search} onChange={(e) => setFilter('q', e.target.value)} placeholder="Name or URL path" />
        <SelectField label="Type" value={kind} onChange={(e) => setFilter('kind', e.target.value)}>
          <option value="">All types</option>
          {pageKinds.map((k) => <option key={k} value={k}>{kindInfo[k].label}</option>)}
        </SelectField>
        <SelectField label="Language" value={locale} onChange={(e) => setFilter('locale', e.target.value)}>
          <option value="">All languages</option>
          {locales.map((l) => <option key={l} value={l}>{l}</option>)}
        </SelectField>
        <SelectField label="Status" value={life} onChange={(e) => setFilter('state', e.target.value)}>
          <option value="">Active pages</option>
          {Object.entries(LIFECYCLE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </SelectField>
        {filtered ? <Button variant="ghost" size="sm" onClick={() => setParams({}, { replace: true })}>Clear filters</Button> : null}
      </form>
      <p className="ag-hint" role="status" aria-live="polite">{q.isSuccess ? `${rows.length} of ${all.length} pages` : ''}</p>

      {q.isLoading ? (
        <div className="ag-table-wrap" role="status">
          <span className="ag-visually-hidden">Loading landings…</span>
          <table className="ag-table"><tbody>{Array.from({ length: 4 }).map((_, i) => <tr key={i}><td><Skeleton width={220} /></td><td><Skeleton width={70} /></td><td><Skeleton width={90} height={22} /></td><td><Skeleton width={120} /></td></tr>)}</tbody></table>
        </div>
      ) : q.isError ? (
        <QueryError error={q.error} onRetry={() => void q.refetch()} />
      ) : all.length === 0 ? (
        <EmptyState title="No landing pages yet" icon="layout" action={can('landings.create') ? <Button variant="primary" icon="plus" onClick={() => setNewOpen(true)}>New landing</Button> : undefined}>
          Create the first page from a template, or run the database seed for the starter site structure.
        </EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState title="No pages match these filters" icon="search" action={<Button onClick={() => setParams({}, { replace: true })}>Clear filters</Button>}>
          Try a different search or status.
        </EmptyState>
      ) : (
        <div className="ag-table-wrap" {...scrollRegion('Landing pages')}>
          <table className="ag-table">
            <caption className="ag-visually-hidden">Landing pages by language and status</caption>
            <thead>
              <tr>
                <th scope="col">Page</th>
                <th scope="col">Type</th>
                <th scope="col">Language</th>
                <th scope="col">Status</th>
                <th scope="col">Translation</th>
                <th scope="col">Live</th>
                <th scope="col">Last edited</th>
                <th scope="col"><span className="ag-visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => {
                const lc = lifecycleOf(l);
                const siblings = all.filter((x) => x.landingId === l.landingId).map((x) => x.locale);
                return (
                  <tr key={l.id} className="ag-row">
                    <td>
                      <span className="ag-cell-2">
                        <span>{l.name}{l.restrictedCategory ? <> <Badge tone="warning">Restricted</Badge></> : null}</span>
                        <span className="ag-mono">/{l.slug}</span>
                      </span>
                    </td>
                    <td>{kindInfo[l.kind].label}</td>
                    <td>{l.locale}</td>
                    <td>
                      <Badge tone={LIFECYCLE[lc].tone}>{LIFECYCLE[lc].label}</Badge>
                      {l.scheduledFor ? <div className="ag-hint">{fmtDate(l.scheduledFor)}</div> : null}
                    </td>
                    <td>{TRANSLATION[l.translationStatus] ?? l.translationStatus}</td>
                    <td className="ag-num">{l.publishedVersionNumber === null ? '—' : `v${l.publishedVersionNumber}`}</td>
                    <td>{fmtDate(l.updatedAt)}</td>
                    <td className="row-actions">
                      <Link to={`/landings/${l.id}`} className="ag-btn ag-btn--sm" aria-label={`${can('landings.edit') ? 'Edit' : 'View'} ${l.name} (${l.locale})`} style={{ textDecoration: 'none' }}>{can('landings.edit') ? 'Edit' : 'View'}</Link>
                      {can('landings.create') || can('landings.publish') || (can('ads.edit') && l.publishedVersionNumber !== null && lc !== 'archived') ? (
                        <RowMenu label={`More actions for ${l.name} (${l.locale})`}>
                          {(close) => (
                            <>
                              {can('ads.edit') && l.publishedVersionNumber !== null && lc !== 'archived' ? <button type="button" onClick={() => { close(); nav(`/advertising/new?landing=${l.landingId}`); }}>Create ad campaign</button> : null}
                              {can('landings.create') ? <button type="button" onClick={() => { close(); setDup(l); }}>Duplicate</button> : null}
                              {can('landings.create') && can('landings.edit') ? <button type="button" onClick={() => { close(); setTr(l); }}>Add a language</button> : null}
                              {can('landings.publish') ? <button type="button" onClick={() => { close(); if (lc === 'archived') archiveM.mutate({ item: l, restore: true }); else setArchive(l); }}>{lc === 'archived' ? 'Restore' : 'Archive'}</button> : null}
                            </>
                          )}
                        </RowMenu>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {archiveM.isError && !archive ? <div style={{ marginTop: 12 }}><Notice tone="error" title="Could not restore">{archiveM.error.message}</Notice></div> : null}

      <NewLandingDrawer open={newOpen} onClose={() => setNewOpen(false)} />
      <DuplicateDrawer item={dup} onClose={() => setDup(null)} />
      <TranslateDrawer item={tr} existing={tr ? all.filter((x) => x.landingId === tr.landingId).map((x) => x.locale) : []} onClose={() => setTr(null)} />
      <Drawer open={archive !== null} onClose={() => { archiveM.reset(); setArchive(null); }} title={`Archive “${archive?.name ?? ''}”?`}
        footer={<><Button onClick={() => { archiveM.reset(); setArchive(null); }}>Cancel</Button><Button variant="danger" loading={archiveM.isPending} onClick={() => archive && archiveM.mutate({ item: archive, restore: false })}>Archive page</Button></>}>
        <p>{archive?.publishedVersionNumber !== null ? 'This page is live. Archiving takes it offline and its address stops resolving. ' : ''}Nothing is deleted: versions, leads and history are kept, and you can restore the page later.</p>
        {archiveM.isError ? <Notice tone="error" title="Could not archive">{archiveM.error.message}</Notice> : null}
      </Drawer>
    </>
  );
}
